import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { delay, Field, Input, STAGGER, TextArea } from '@caios/ui';
import { loadFonts, resolveTheme, themeVars, type BookingTheme } from '../booking-theme.ts';

/**
 * Public booking pages (Wave A5). Times in the visitor's own zone. Themed per booking page and, when embedded
 * on a client's website (?embed=1, via /embed.js), per placement — same attributes as MeetPoint's embed.
 */
const TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;
const get = async <T,>(p: string) => { const r = await fetch(p); return { ok: r.ok, data: (await r.json().catch(() => ({}))) as T }; };
const post = async <T,>(p: string, b: unknown) => { const r = await fetch(p, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(b) }); return { ok: r.ok, data: (await r.json().catch(() => ({}))) as T }; };
const dayKey = (iso: string) => new Date(iso).toLocaleDateString('en-CA', { timeZone: TZ });
const dayLabel = (k: string) => new Date(`${k}T12:00:00`).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
const timeLabel = (iso: string) => new Date(iso).toLocaleTimeString(undefined, { timeZone: TZ, hour: 'numeric', minute: '2-digit' });
const whenLabel = (iso: string) => new Date(iso).toLocaleString(undefined, { timeZone: TZ, weekday: 'long', day: 'numeric', month: 'long', hour: 'numeric', minute: '2-digit' });
// randomUUID only exists in secure contexts; a frame inside a non-HTTPS client site doesn't have it.
const requestId = () => ((globalThis.crypto as Partial<Crypto> | undefined)?.randomUUID ? crypto.randomUUID() : '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, (c) => (Number(c) ^ (crypto.getRandomValues(new Uint8Array(1))[0]! & (15 >> (Number(c) / 4)))).toString(16)));
const toParent = (msg: Record<string, unknown>) => { if (window.parent !== window) for (const p of ['caios', 'meetpoint']) window.parent.postMessage({ ...msg, type: `${p}:${msg.type}` }, '*'); };

function Shell({ title, saved, children }: { title: string; saved?: Record<string, unknown> | undefined; children: ReactNode }) {
  const { theme, embed } = useMemo(() => resolveTheme(saved, window.location.search), [saved]);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => { loadFonts(theme); }, [theme]);
  useEffect(() => {
    if (!embed) return;
    // Match the host page's colour scheme: a frame whose scheme differs gets an opaque backdrop (dark corners).
    document.documentElement.style.colorScheme = 'normal';
    document.documentElement.style.background = 'transparent'; document.body.style.background = 'transparent';
    const send = () => toParent({ type: 'height', height: Math.ceil(ref.current?.getBoundingClientRect().height ?? 0) + 2 });
    const ro = new ResizeObserver(send); if (ref.current) ro.observe(ref.current); send();
    return () => ro.disconnect();
  }, [embed]);
  return (
    <div ref={ref} className={`cx-app bk${embed ? ' bk--embed' : ''}`} data-theme={(theme as BookingTheme).mode === 'dark' ? 'tide' : 'arctic'} style={themeVars(theme)}>
      <section className="bk-card cx-rise">
        {!theme.hideBusiness && <div className="cx-eyebrow">{title} · <b>book a time</b></div>}
        {children}
      </section>
    </div>
  );
}
const H = ({ children }: { children: ReactNode }) => <h1 className="bk-title">{children}</h1>;

export function BookingPage() {
  const { org, page } = useParams({ from: '/book/$org/$page' });
  const q = useQuery({ queryKey: ['bookpage', org, page], queryFn: () => get<{ page: { name: string; theme?: Record<string, unknown> }; types: { slug: string; name: string; description: string; duration: number }[] }>(`/api/v1/book/${org}/${page}`) });
  if (!q.data) return <Shell title="…"><p className="cx-body">Loading…</p></Shell>;
  if (!q.data.ok) return <Shell title="Booking"><H>This booking page doesn’t exist</H></Shell>;
  const d = q.data.data;
  return (
    <Shell title={d.page.name} saved={d.page.theme}>
      <H>Pick a meeting</H>
      <div className="bk-types">{d.types.map((t, i) => (
        <a key={t.slug} className="bk-type cx-rise" style={delay(i, STAGGER.list)} href={`/book/${org}/${page}/${t.slug}${window.location.search}`} data-testid="book-type">
          <b>{t.name}</b><span className="cx-mono">{t.duration} min</span>{t.description && <p>{t.description}</p>}
        </a>))}
      </div>
    </Shell>
  );
}

export function BookingType() {
  const { org, page, type } = useParams({ from: '/book/$org/$page/$type' });
  return <SlotPicker load={`/api/v1/book/${org}/${page}/${type}/slots`} submit={(start, f) => post<{ host: string; start: string; managePath: string; error?: string }>(`/api/v1/book/${org}/${page}/${type}`, { start, ...f, timezone: TZ, idempotencyKey: requestId() })} />;
}

/** Old MeetPoint links: /schedule/<business>/<type> → the same page on CAIOS (org resolved from the page slug). */
export function ScheduleRedirect() {
  const p = useParams({ strict: false }) as { page: string; type?: string };
  const navigate = useNavigate();
  const [missing, setMissing] = useState(false);
  useEffect(() => {
    get<{ org: string }>(`/api/v1/book-resolve/${encodeURIComponent(p.page)}`).then((r) => {
      if (!r.ok) { setMissing(true); return; }
      window.location.replace(`/book/${r.data.org}/${p.page}${p.type ? `/${p.type}` : ''}${window.location.search}`);
    });
  }, [p.page, p.type, navigate]);
  return <Shell title="Booking">{missing ? <H>This booking page doesn’t exist</H> : <p className="cx-body">Loading…</p>}</Shell>;
}

function SlotPicker({ load, submit, reschedule }: { load: string; submit: (start: string, f: { name: string; email: string; notes: string }) => Promise<{ ok: boolean; data: { host: string; start: string; managePath: string; error?: string } }>; reschedule?: boolean }) {
  const q = useQuery({ queryKey: ['slots', load], queryFn: () => get<{ slots: string[]; type?: { name: string; duration: number; description: string }; page?: { name: string; theme?: Record<string, unknown> } }>(load) });
  const slots = useMemo(() => q.data?.data.slots ?? [], [q.data]);
  const days = useMemo(() => [...new Set(slots.map(dayKey))], [slots]);
  const [day, setDay] = useState<string | null>(null);
  const [start, setStart] = useState<string | null>(null);
  const [f, setF] = useState({ name: '', email: '', notes: '' });
  const [done, setDone] = useState<{ host: string; start: string; managePath: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const activeDay = day ?? days[0] ?? null;
  async function go(e?: FormEvent) {
    e?.preventDefault(); setErr(null); setBusy(true);
    const r = await submit(start!, f);
    setBusy(false);
    if (!r.ok) { setErr(r.data.error ?? 'That didn’t work.'); setStart(null); q.refetch(); return; }
    setDone(r.data);
    toParent({ type: 'booked', start: r.data.start, meetingName: q.data?.data.type?.name });
  }
  const title = q.data?.data.page?.name ?? 'Booking';
  const saved = q.data?.data.page?.theme;
  if (done) return (
    <Shell title={title} saved={saved}>
      <H>You’re booked{reschedule ? ' (rescheduled)' : ''}</H>
      <p className="cx-body"><b style={{ color: 'var(--cx-text)' }}>{whenLabel(done.start)}</b> with {done.host}. Times shown in {TZ}.</p>
      <p className="cx-body">A confirmation with the meeting link is on its way to your email.</p>
      <div className="bk-note"><span className="cx-label">Keep this link to reschedule or cancel</span><a href={done.managePath} data-testid="manage-link">{window.location.origin}{done.managePath}</a></div>
    </Shell>
  );
  return (
    <Shell title={title} saved={saved}>
      {q.data?.data.type && <H>{q.data.data.type.name} · {q.data.data.type.duration} min</H>}
      {reschedule && <H>Pick a new time</H>}
      <p className="cx-label">Times in {TZ.replace(/_/g, ' ')}</p>
      {!slots.length && q.isSuccess && <p className="cx-body">No open times right now.</p>}
      <div className="bk-days" role="tablist">{days.slice(0, 14).map((d) => <button key={d} type="button" role="tab" aria-selected={d === activeDay} className="bk-day" onClick={() => { setDay(d); setStart(null); }} data-testid="book-day">{dayLabel(d)}</button>)}</div>
      <div className="bk-times">{slots.filter((x) => dayKey(x) === activeDay).map((x, i) => (
        <button key={x} type="button" aria-pressed={start === x} className="bk-time cx-rise" style={delay(i, 25)} onClick={() => setStart(x)} data-testid="book-time">{timeLabel(x)}</button>))}
      </div>
      {start && !reschedule && (<form className="bk-form cx-rise" onSubmit={go}>
        <p className="cx-body" style={{ margin: 0 }}><b style={{ color: 'var(--cx-text)' }}>{whenLabel(start)}</b></p>
        <div className="bk-two">
          <Field label="Your name"><Input required autoComplete="name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="book-name" /></Field>
          <Field label="Email"><Input required type="email" autoComplete="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="book-email" /></Field>
        </div>
        <Field label="Anything we should know? (optional)"><TextArea rows={3} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></Field>
        <button type="submit" className="cx-btn cx-btn--primary bk-wide" disabled={busy} data-testid="book-confirm">{busy ? 'Booking…' : 'Confirm booking'}</button>
      </form>)}
      {start && reschedule && <button type="button" className="cx-btn cx-btn--primary bk-wide" disabled={busy} onClick={() => go()} data-testid="book-confirm">Move to {whenLabel(start)}</button>}
      {err && <div className="cx-note cx-note--fire" role="alert">{err}</div>}
    </Shell>
  );
}

export function ManageBooking() {
  const { token } = useParams({ from: '/book/manage/$token' });
  const q = useQuery({ queryKey: ['manage', token], queryFn: () => get<{ status: string; start: string; host: string; meetUrl: string | null; type: { name: string; page: string; theme?: Record<string, unknown> } }>(`/api/v1/book/manage/${token}`) });
  const [mode, setMode] = useState<'view' | 'reschedule' | 'cancelled'>('view');
  if (!q.data) return <Shell title="…"><p className="cx-body">Loading…</p></Shell>;
  if (!q.data.ok) return <Shell title="Booking"><H>This link isn’t valid any more</H></Shell>;
  const b = q.data.data;
  if (mode === 'reschedule') return <SlotPicker reschedule load={`/api/v1/book/manage/${token}/slots`} submit={(start) => post(`/api/v1/book/manage/${token}/reschedule`, { start })} />;
  return (
    <Shell title={b.type.page} saved={b.type.theme}>
      <H>{b.type.name}</H>
      <p className="cx-body"><b style={{ color: 'var(--cx-text)' }}>{whenLabel(b.start)}</b> with {b.host}. Times shown in {TZ}.</p>
      {b.meetUrl && b.status === 'confirmed' && mode !== 'cancelled' && <p className="cx-body">Google Meet: <a href={b.meetUrl} data-testid="manage-meet">{b.meetUrl.replace(/^https?:\/\//, '')}</a></p>}
      {(b.status === 'cancelled' || mode === 'cancelled') ? <div className="cx-note cx-note--fire">This booking is cancelled.</div> : (
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" className="cx-btn" onClick={() => setMode('reschedule')} data-testid="manage-reschedule">Reschedule</button>
          <button type="button" className="cx-btn" onClick={async () => { if ((await post(`/api/v1/book/manage/${token}/cancel`, {})).ok) setMode('cancelled'); }} data-testid="manage-cancel">Cancel booking</button>
        </div>)}
    </Shell>
  );
}
